import React from 'react'
import { Card, CardHeader, CardContent, Box, Typography } from '@mui/material'
import { AccountDto } from '../../interactors/gen/backendClient'
import { spacingItem } from '../theme'
import { useAccountsStore } from '../../store/AccountsStore'
import { EditMemberHomePrompt } from './EditMemberHomePrompt'
import {
  getReadableAccountHomeBalcony,
  getReadableAccountHomeGarden,
  getReadableAccountHomeType,
  getReadableAccountPreferencesWillingToSocializeWildAnimals,
} from '../../domain/Account/AccountDisplay'
import { MemberHomeAnimals } from './MemberHomeAnimals'

interface Props {
  account: AccountDto
  onSubmit: (data: Partial<AccountDto>) => Promise<void>
  isOwnAccount: boolean
  isLoading: boolean
}

export const MemberHomeCard: React.FC<Props> = ({ account, onSubmit, isOwnAccount, isLoading }) => {
  const accountsStore = useAccountsStore()

  const labelSx = { fontSize: 12, fontWeight: 800, color: '#7A8197', lineHeight: 1.2 }
  const valueSx = { fontSize: 14, fontWeight: 700, color: '#1F3764', lineHeight: 1.25 }
  const rowSx = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: '220px 1fr' },
    columnGap: 2,
    rowGap: 0.5,
    alignItems: 'baseline',
    py: 1,
    borderBottom: '1px solid rgba(122, 129, 151, 0.14)',
    '&:last-of-type': { borderBottom: 'none' },
  }

  return (
    <Card sx={{ ...spacingItem, height: 'calc(100% - 16px)', boxSizing: 'border-box' }}>
      <CardHeader title="Foyer et Habitation" />
      {account.restrainedData ? (
        <CardContent>
          <Typography>
            Ces informations ne sont pas visibles compte tenu des préférences de l&#39;utilisateur
          </Typography>
        </CardContent>
      ) : (
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', pl: { xs: 0, sm: 2, md: 6 } }}>
              <Box sx={rowSx}>
                <Typography sx={labelSx}>Type d&#39;habitation</Typography>
                <Typography sx={valueSx}>
                  {account.home?.homeType ? getReadableAccountHomeType(account.home?.homeType) : 'Non renseigné'}
                </Typography>
              </Box>

              <Box sx={rowSx}>
                <Typography sx={labelSx}>Balcon</Typography>
                <Typography sx={valueSx}>
                  {account.home?.balcony ? getReadableAccountHomeBalcony(account.home?.balcony) : 'Non renseigné'}
                </Typography>
              </Box>

              <Box sx={rowSx}>
                <Typography sx={labelSx}>Jardin</Typography>
                <Typography sx={valueSx}>
                  {account.home?.garden ? getReadableAccountHomeGarden(account.home?.garden) : 'Non renseigné'}
                </Typography>
              </Box>

              <Box sx={rowSx}>
                <Typography sx={labelSx}>Salles de quarantaine</Typography>
                <Typography sx={valueSx}>
                  {account.home?.quarantineRoomNumber || account.home?.quarantineRoomNumber === 0
                    ? account.home?.quarantineRoomNumber
                    : 'Non renseigné'}
                </Typography>
              </Box>

              <Box sx={rowSx}>
                <Typography sx={labelSx}>Possibilité d&#39;isolement</Typography>
                <Typography sx={valueSx}>
                  {account.home?.hasIsolationRoom
                    ? getReadableAccountPreferencesWillingToSocializeWildAnimals(account.home?.hasIsolationRoom)
                    : 'Non renseigné'}
                </Typography>
              </Box>

              <Box sx={rowSx}>
                <Typography sx={labelSx}>Véhiculé</Typography>
                <Typography sx={valueSx}>
                  {account.home?.hasCar
                    ? getReadableAccountPreferencesWillingToSocializeWildAnimals(account.home?.hasCar)
                    : 'Non renseigné'}
                </Typography>
              </Box>

              <Box sx={rowSx}>
                <Typography sx={labelSx}>Surface du foyer</Typography>
                <Typography sx={valueSx}>{account.home?.homeSurface ?? 'Non renseigné'}</Typography>
              </Box>

              <Box sx={rowSx}>
                <Typography sx={labelSx}>Enfants</Typography>
                <Typography sx={valueSx}>
                  {account.home?.childrenNumber || account.home?.childrenNumber === 0
                    ? account.home?.childrenNumber
                    : 'Non renseigné'}
                </Typography>
              </Box>

              <Box sx={rowSx}>
                <Typography sx={labelSx}>Adultes</Typography>
                <Typography sx={valueSx}>
                  {account.home?.adultNumber || account.home?.adultNumber === 0
                    ? account.home?.adultNumber
                    : 'Non renseigné'}
                </Typography>
              </Box>

              <Box sx={rowSx}>
                <Typography sx={labelSx}>Capacité d&#39;accueil</Typography>
                <Typography sx={valueSx}>
                  {account.home?.maxCapacity
                    ? `${account.home.maxCapacity} ${account.home.maxCapacity > 1 ? 'animaux' : 'animal'}`
                    : 'Non renseigné'}
                </Typography>
              </Box>
            </Box>
            {accountsStore.canEdit() && (
              <Box
                sx={{
                  width: '44px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  borderLeft: '1px solid rgba(122, 129, 151, 0.18)',
                  ml: 1,
                }}
              >
                <EditMemberHomePrompt
                  account={account}
                  onSubmit={onSubmit}
                  isOwnAccount={isOwnAccount}
                  isLoading={isLoading}
                />
              </Box>
            )}
          </Box>
          <MemberHomeAnimals account={account} onSubmit={onSubmit} isOwnAccount={isOwnAccount} />
        </CardContent>
      )}
    </Card>
  )
}
